<script setup>
import { computed, reactive } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { useI18n } from 'vue-i18n'
import { useStore } from 'vuex'
import Loading from '../../../components/Loading.vue'
import {
  KkIcon,
  KkModal
} from '../../../components/base'
import { KkInfo, KkListWithMenu } from '../../../components/ui'
import { KkTutorial } from '../../../components/projects'
import cloneDeep from 'lodash.clonedeep'
import StatusBadge from '../../../components/StatusBadge'
import searchGroupImagePath from '../../../../../images/mapion_biz/search_group'
const store = useStore()
const { t } = useI18n()
const router = useRouter()
const searchGroups = computed(() => store.state.companiesMapionBizSearchGroups.data)
const searchGroupLoading = computed(() => store.state.companiesMapionBizSearchGroups.loading)
const updateLoading = computed(() => store.state.companiesMapionBizSearchGroupsOrders.loading)
const readMapionBizSearchGroups = () => store.dispatch('companiesMapionBizSearchGroups/readMapionBizSearchGroups')
const deleteMapionBizSearchGroup = (searchGroupId) => store.dispatch('companiesMapionBizSearchGroups/deleteMapionBizSearchGroup', searchGroupId)
const updateMapionBizSearchGroupOrders = (payload) => store.dispatch('companiesMapionBizSearchGroupsOrders/updateMapionBizSearchGroupOrders', payload)
const { company_uuid } = useRoute().params
const OPEN_STATUS = 1
const CLOSE_STATUS = 2
const state = reactive({
  searchGroups: [],
  showEditMenu: false,
  orderSearchGroups: false,
  modal: false,
  modalStoreNames: [],
  openEditSearchGroupId: 0
})

const menuList =   [
  {
    type: 'edit',
    text: t('labels.edit')
  },
  {
    type: 'storeSetting',
    text: t('pages.companies.mapionBizSearchGroups.storeSetting')
  },
  {
    type: 'moveTop',
    text: t('labels.moveTop')
  },
  {
    type: 'moveOneUp',
    text: t('labels.moveOneUp')
  },
  {
    type: 'moveOneDown',
    text: t('labels.moveOneDown')
  },
  {
    type: 'moveBottom',
    text: t('labels.moveBottom')
  },
  {
    type: 'delete',
    text: t('labels.delete')
  },
]

const modalOpen = (condition) => {
  state.modalStoreNames = condition.stores.map(v => v.name)
  state.modal = true
}

const modalClose = () => {
  state.modalStoreNames = []
  state.modal = false
}

const handleClickMenu = ({ type, index }) => {
  const searchGroup = state.searchGroups[index]
  if(type === 'edit') router.push({ name: 'CompaniesMapionBizSearchGroupsEdit', params: { company_uuid: company_uuid, search_group_id: searchGroup.id } })
  if(type === 'delete') {
    if(!confirm(t('pages.companies.mapionBizSearchGroups.deleteConfirm'))) return
    deleteMapionBizSearchGroup(searchGroup.id)
  }
  if(type === 'storeSetting') router.push({ name: 'CompaniesMapionBizSearchGroupsStoreEdit', params: { company_uuid: company_uuid, search_group_id: searchGroup.id } })

  if (type === 'moveOneUp' && index === 0) return
  if (type === 'moveBottom' && index === state.searchGroups.length - 1) return
  state.searchGroups.splice(index, 1)
  state.orderSearchGroups = true
  switch (type) {
  case 'moveTop':
    state.searchGroups.unshift(searchGroup)
    break
  case 'moveOneUp':
    if (index === 0) return
    state.searchGroups.splice(index - 1, 0, searchGroup)
    break
  case 'moveOneDown':
    state.searchGroups.splice(index + 1, 0, searchGroup)
    break
  case 'moveBottom':
    state.searchGroups.push(searchGroup)
    break
  }
}

const handleClickOrderEnter = async() => {
  const orderIds = state.searchGroups.map(v => Number(v.id))
  const isSuccess = await updateMapionBizSearchGroupOrders({ search_group_ids: orderIds })
  if(isSuccess) state.orderSearchGroups = false
}

const load = async () => {
  await readMapionBizSearchGroups()
  state.searchGroups = cloneDeep(searchGroups)
}

load()
</script>

<template>
  <div>
    <div class="p-box">
      <Loading v-if="searchGroupLoading || updateLoading" />
      <KkTutorial
        v-else-if="state.searchGroups.length === 0"
        class="noSearchGroups"
        :description1="t('pages.companies.mapionBizSearchGroups.noSearchGroups.notice')"
        :img-path="searchGroupImagePath"
        :tutorial-description="t('pages.companies.mapionBizSearchGroups.noSearchGroups.description')"
      >
        <template #buttonArea>
          <div class="c-button noSearchGroups__button">
            <router-link
              :to="{ name: 'CompaniesManagement', params: { company_uuid: company_uuid } }"
              class="c-button__item c-button__item--outline-cancel"
            >
              {{ $t('labels.cancel') }}
            </router-link>
          </div>
          <div class="c-button noSearchGroups__button">
            <router-link :to="{ name: 'CompaniesMapionBizSearchGroupsNew', params: { company_uuid: company_uuid }}"
                         class="c-button__item c-button__item--primary"
            >
              {{ t('pages.companies.mapionBizSearchGroups.noSearchGroups.add') }}
            </router-link>
          </div>
        </template>
      </KkTutorial>
      <template v-else>
        <div class="p-box__header alignRight">
          <div class="c-button">
            <button
              class="c-button__item c-button__item--primary"
              :class="{ 'c-button__item--disabled': !state.orderSearchGroups }"
              :disabled="!state.orderSearchGroups"
              @click="handleClickOrderEnter"
            >
              {{ t('pages.companies.mapionBizSearchGroups.enter') }}
            </button>
          </div>
        </div>
        <div class="p-box__body">
          <KkInfo icon="hint">
            {{ t('pages.companies.mapionBizSearchGroups.info') }}
          </KkInfo>
          <div class="p-table__content">
            <table class="searchGroupTable">
              <thead>
                <tr>
                  <th class="searchGroupTable__name">
                    {{ t('pages.companies.mapionBizSearchGroups.searchGroupName') }}
                  </th>
                  <th class="searchGroupTable__condition">
                    {{ t('pages.companies.mapionBizSearchGroups.condition') }}
                  </th>
                  <th class="searchGroupTable__conditionType">
                    {{ t('pages.companies.mapionBizSearchGroups.conditionType.label') }}
                  </th>
                  <th class="searchGroupTable__selectType">
                    {{ t('pages.companies.mapionBizSearchGroups.selectType.label') }}
                  </th>
                  <th />
                </tr>
              </thead>
              <tbody>
                <tr
                  v-for="(searchGroup, i) in state.searchGroups"
                  :key="'searchGroup_' + i"
                >
                  <td>
                    <div>
                      <span class="conditionContainer__name">{{ searchGroup.name }}</span>
                      <div class="conditionContainer__status">
                        <StatusBadge :status="searchGroup.isPublished ? OPEN_STATUS : CLOSE_STATUS" />
                        {{ t(`pages.companies.mapionBizSearchGroups.${ searchGroup.isPublished ? 'open' : 'close' }`) }}
                      </div>
                      <div v-if="searchGroup.isMappingIcon" class="mappingIconBadge">
                        {{ t('pages.companies.mapionBizSearchGroups.mappingIconBadge') }}
                      </div>
                    </div>
                  </td>
                  <td>
                    <div v-for="(condition, j) in searchGroup.conditions"
                         :key="'condition' + j"
                         class="conditionContainer"
                         :class="{ 'conditionContainer--closed': !searchGroup.isPublished || !condition.isPublished }"
                    >
                      <div class="conditionContainer__wrap">
                        <div v-if="condition.icon" class="conditionContainer__image">
                          <img :src="condition.icon" alt="">
                        </div>
                        <div class="conditionContainer__contents">
                          <div v-if="condition.stores.length === 0">
                            <span v-if="condition.isPublished">{{ $t('pages.companies.mapionBizSearchGroups.noStore', { name: condition.name }) }}</span>
                            <span v-else>{{ $t('pages.companies.mapionBizSearchGroups.conditionCloseNoStore', { name: condition.name }) }}</span>
                          </div>
                          <div v-else>
                            {{ condition.name }}
                            <button v-if="condition.isPublished" @click="modalOpen(condition)">{{ $t('pages.companies.mapionBizSearchGroups.storeCount', { number: condition.stores.length }) }}</button>
                            <button v-else @click="modalOpen(condition)">{{ $t('pages.companies.mapionBizSearchGroups.conditionCloseStoreCount', { number: condition.stores.length }) }}</button>
                          </div>
                        </div>
                      </div>
                    </div>
                  </td>
                  <td class="alignTop">
                    {{ t(`pages.companies.mapionBizSearchGroups.conditionType.${ searchGroup.conditionType }`) }}
                  </td>
                  <td class="alignTop">
                    {{ t(`pages.companies.mapionBizSearchGroups.selectType.${ searchGroup.selectType }`) }}
                  </td>
                  <td>
                    <KkListWithMenu
                      :menu-list="menuList"
                      :open="state.openEditSearchGroupId === searchGroup.id"
                      @on-click-edit-menu="e => handleClickMenu({ type: e, index: i })"
                      @open="state.openEditSearchGroupId = searchGroup.id"
                      @close="state.openEditSearchGroupId = 0"
                    />
                  </td>
                </tr>
              </tbody>
            </table>
            <router-link :to="{ name: 'CompaniesMapionBizSearchGroupsNew', params: { company_uuid: company_uuid } }">
              <button class="addSearchGroup" type="button">
                <KkIcon name="iconPlusRed" />
                <span class="addLabel">{{ $t('pages.companies.mapionBizSearchGroups.addLabel') }}</span>
              </button>
            </router-link>
          </div>
        </div>
        <KkModal :is-visible="state.modal" @change="modalClose">
          <div class="content__header">
            {{ $t('pages.companies.mapionBizSearchGroups.modalTitle') }}
          </div>
          <div class="content__body">
            <div v-for="(name, index) in state.modalStoreNames" :key="index" class="item">
              {{ name }}
            </div>
          </div>
          <div class="content__footer">
            <div class="c-button">
              <button
                class="c-button__item c-button__item--outline-cancel"
                @click="modalClose"
              >
                {{ $t('labels.close') }}
              </button>
            </div>
          </div>
        </KkModal>
      </template>
    </div>
  </div>
</template>

<style lang="scss" scoped>
@import "../../../../../stylesheets/biz_spa/foundation/_var.scss";
@import "../../../../../stylesheets/biz_spa/foundation/_mixin.scss";
.unpublished {
  color: $color-accent-primary;
}
.searchGroupTable {
  border-spacing: 0 8px !important;
  &__name {
    width: 25%;
  }
  &__condition {
    width: 45%;
  }
  &__conditionType {
    width: 10%;
  }
  &__selectType {
    width: 15%;
  }
  thead {
    display: none;
  }
  td {
    display: block;
    width: 100%;
    border: 1px solid #E4E7EB;
    font-size: 12px;
  }
  td:not(:first-child) {
    border-top: none;
  }
  td:not(:last-child) {
    border-bottom: none;
  }
  &__tr {
    margin-top: 16px;
  }
  &__tr--closed td {
    background: $color-base-i;
  }
  .conditionContainer {
    margin-bottom: 10px;
    padding: 12px;
    border: 1px solid #E4E7EB;
    border-radius: 4px;
    &__wrap {
      display: flex;
      align-items: center;
    }
    &__name {
      font-weight: bold;
      word-break: break-all;
    }
    &__contents {
      display: flex;
      align-items: center;
      width: 80%;
      font-size: 14px;
      word-break: break-all;
    }
    &__image {
      margin-right: 10px;
      img {
        width: 60px;
        height: 60px;
        border: 1px solid #E4E7EB;
        border-radius: 4px;
      }
    }
    &--closed {
      background: $color-base-i;
      img {
        filter: grayscale(1) opacity(.4);
      }
      .conditionContainer__contents {
        color: #ABABAB;
      }
    }
    .modalButton {
      text-decoration: underline;
    }
  }
  @include mq() {
    thead {
      display: table-header-group;
    }
    td {
      display: table-cell;
      width: auto;
    }
    &__tr {
      vertical-align: top;
    }
    td:not(:first-child) {
      border-top: 1px solid #E4E7EB;
      border-left: none;
    }
    td:not(:last-child) {
      border-bottom: 1px solid #E4E7EB;
      border-right: none;
    }
  }
}
.addSearchGroup {
  width: 100%;
  height: 160px;
  margin-top: 32px;
  border-radius: 4px;
  background: $color-base-i;
  color: $color-accent-primary;
  text-align: center;
  svg {
    display: block;
    width: 32px;
    height: 32px;
    margin: 0 auto;
  }
}
.addLabel {
  color: $color-accent-primary;
}
.p-table__content {
  overflow-x: visible;
}
.mappingIconBadge {
  display: inline-block;
  margin-top: 5px;
  padding: 4px 6px;
  border-radius: 12px;
  background: #414141;
  color: #FFF;
  font-weight: bold;
  font-size: 10px;
}
.alignTop {
  vertical-align: top;
}
.alignRight {
  justify-content: end;
}
.linkToAddSearchGroups {
  margin-top: 15px;
}
.content {
  &__header {
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    padding: 20px 32px;
    border-bottom: 1px solid $color-base-c;
    font-size: 16px;
    line-height: 1.5;
  }
  &__footer {
    display: flex;
    justify-content: flex-end;
    padding: 32px;
    border-top: 1px solid $color-base-c;
  }
  &__body {
    height: 50vh;
    padding: 32px;
    overflow-y: scroll;
    .item {
      margin-bottom: 10px;
    }
  }
}
.noSearchGroups {
  &__button {
    .c-button__item {
      margin: 0;
    }
  }
}
</style>
